import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Box,
  Stack,
} from '@mui/material';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import { LeadFormData, LeadStatus, LeadPriority, LeadSource } from '../../types/lead.types';

const leadSchema = Yup.object().shape({
  first_name: Yup.string().required('First name is required'),
  last_name: Yup.string().required('Last name is required'),
  email: Yup.string().email('Invalid email'),
  phone: Yup.string(),
  company: Yup.string(),
  position: Yup.string(),
  status: Yup.string().required('Status is required'),
  priority: Yup.string().required('Priority is required'),
  source: Yup.string(),
  estimated_value: Yup.number().min(0, 'Value must be positive'),
  notes: Yup.string(),
});

interface LeadFormProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (values: LeadFormData) => void;
  initialValues?: Partial<LeadFormData>;
  title: string;
  isSubmitting?: boolean;
}

const defaultValues: LeadFormData = {
  first_name: '',
  last_name: '',
  email: '',
  phone: '',
  company: '',
  position: '',
  status: 'new',
  priority: 'medium',
  source: 'website',
  estimated_value: 0,
  notes: '',
  tags: [],
};

const statusOptions: { value: LeadStatus; label: string }[] = [
  { value: 'new', label: 'New' },
  { value: 'contacted', label: 'Contacted' },
  { value: 'qualified', label: 'Qualified' },
  { value: 'proposal', label: 'Proposal' },
  { value: 'negotiation', label: 'Negotiation' },
  { value: 'won', label: 'Won' },
  { value: 'lost', label: 'Lost' },
];

const priorityOptions: { value: LeadPriority; label: string }[] = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'urgent', label: 'Urgent' },
];

const sourceOptions: { value: LeadSource; label: string }[] = [
  { value: 'website', label: 'Website' },
  { value: 'referral', label: 'Referral' },
  { value: 'linkedin', label: 'LinkedIn' },
  { value: 'cold-call', label: 'Cold Call' },
  { value: 'email-campaign', label: 'Email Campaign' },
  { value: 'event', label: 'Event' },
  { value: 'partner', label: 'Partner' },
  { value: 'other', label: 'Other' },
];

export const LeadForm: React.FC<LeadFormProps> = ({
  open,
  onClose,
  onSubmit,
  initialValues,
  title,
  isSubmitting = false,
}) => {
  // Ensure all values are strings (not null) to avoid React warnings
  const formValues = {
    ...defaultValues,
    ...initialValues,
    // Convert null values to empty strings
    email: initialValues?.email || '',
    phone: initialValues?.phone || '',
    company: initialValues?.company || '',
    position: initialValues?.position || '',
    notes: initialValues?.notes || '',
    source: initialValues?.source || 'website',
    estimated_value: initialValues?.estimated_value || 0,
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      disableEnforceFocus
      disableRestoreFocus
    >
      <DialogTitle>{title}</DialogTitle>
      <Formik
        initialValues={formValues}
        validationSchema={leadSchema}
        onSubmit={onSubmit}
        enableReinitialize
      >
        {({ errors, touched }) => (
          <Form>
            <DialogContent>
              <Stack spacing={2} sx={{ mt: 1 }}>
                <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
                  <Field name="first_name">
                    {({ field }: any) => (
                      <TextField
                        {...field}
                        fullWidth
                        label="First Name *"
                        error={touched.first_name && Boolean(errors.first_name)}
                        helperText={touched.first_name && errors.first_name}
                      />
                    )}
                  </Field>
                  <Field name="last_name">
                    {({ field }: any) => (
                      <TextField
                        {...field}
                        fullWidth
                        label="Last Name *"
                        error={touched.last_name && Boolean(errors.last_name)}
                        helperText={touched.last_name && errors.last_name}
                      />
                    )}
                  </Field>
                </Box>

                <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
                  <Field name="email">
                    {({ field }: any) => (
                      <TextField
                        {...field}
                        fullWidth
                        label="Email"
                        type="email"
                        error={touched.email && Boolean(errors.email)}
                        helperText={touched.email && errors.email}
                      />
                    )}
                  </Field>
                  <Field name="phone">
                    {({ field }: any) => (
                      <TextField {...field} fullWidth label="Phone" />
                    )}
                  </Field>
                </Box>

                <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
                  <Field name="company">
                    {({ field }: any) => (
                      <TextField {...field} fullWidth label="Company" />
                    )}
                  </Field>
                  <Field name="position">
                    {({ field }: any) => (
                      <TextField {...field} fullWidth label="Position" />
                    )}
                  </Field>
                </Box>

                <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
                  <Field name="status">
                    {({ field }: any) => (
                      <TextField
                        {...field}
                        select
                        fullWidth
                        label="Status *"
                        error={touched.status && Boolean(errors.status)}
                        helperText={touched.status && errors.status}
                      >
                        {statusOptions.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </TextField>
                    )}
                  </Field>
                  <Field name="priority">
                    {({ field }: any) => (
                      <TextField
                        {...field}
                        select
                        fullWidth
                        label="Priority *"
                        error={touched.priority && Boolean(errors.priority)}
                        helperText={touched.priority && errors.priority}
                      >
                        {priorityOptions.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </TextField>
                    )}
                  </Field>
                </Box>

                <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
                  <Field name="source">
                    {({ field }: any) => (
                      <TextField {...field} select fullWidth label="Source">
                        {sourceOptions.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </TextField>
                    )}
                  </Field>
                  <Field name="estimated_value">
                    {({ field }: any) => (
                      <TextField
                        {...field}
                        fullWidth
                        label="Estimated Value"
                        type="number"
                        error={touched.estimated_value && Boolean(errors.estimated_value)}
                        helperText={touched.estimated_value && errors.estimated_value}
                      />
                    )}
                  </Field>
                </Box>

                <Field name="notes">
                  {({ field }: any) => (
                    <TextField {...field} fullWidth label="Notes" multiline rows={4} />
                  )}
                </Field>
              </Stack>
            </DialogContent>

            <DialogActions>
              <Button onClick={onClose} color="inherit">
                Cancel
              </Button>
              <Button type="submit" variant="contained" disabled={isSubmitting}>
                {isSubmitting ? 'Saving...' : 'Save Lead'}
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};